import { useEffect, useState } from 'react'
import { useSelectionStore } from '@/stores/selection-store'

export interface TimeAxisSidebarState {
  /** Whether the investigation detail sidebar is currently open. */
  sidebarOpen: boolean
  /** Imperative setter, wired to the sidebar open-change handlers. */
  setSidebarOpen: (open: boolean) => void
}

/**
 * Owns the time-axis view's sidebar visibility.
 *
 * Starts expanded so the entity-detail panel is visible on entry, then
 * force-opens the sidebar whenever an entity or edge is selected *from the
 * time-axis*. Gating on `lastInteractionView === 'timeline'` keeps it from
 * reacting to selections that originated in the graph/map views. Mirrors
 * graph/map behaviour so users always see the matching detail row after
 * clicking on the timeline. Manual expand/collapse still flows through
 * `setSidebarOpen`.
 */
export function useTimeAxisSidebar(): TimeAxisSidebarState {
  const [sidebarOpen, setSidebarOpen] = useState(true)

  // Collapse the store read to a stable primitive so the effect only re-runs
  // when a timeline-originated selection appears or clears.
  const timelineSelection = useSelectionStore(s =>
    s.lastInteractionView === 'timeline' && (s.selectedEntityIds.size > 0 || s.selectedEdgeId)
      ? 'active'
      : null
  )

  useEffect(() => {
    if (timelineSelection) setSidebarOpen(true)
  }, [timelineSelection])

  return { sidebarOpen, setSidebarOpen }
}
